import { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { fmtDate } from '../lib/localeUtils';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Batch labels are printed as a month and year ("MAR 2027", "03/27"), so expiry
// is typed by hand and resolved to the last day of that month. Full dates typed
// in any common order are kept exactly as given.
// Returns '' for empty input and null when it cannot be understood.
export const parseExpiryInput = (raw) => {
  const s = (raw || '').trim();
  if (!s) return '';
  const endOfMonth = (y, m) => {
    const year = Number(y) < 100 ? 2000 + Number(y) : Number(y);
    const month = Number(m);
    if (!month || month > 12) return null;
    return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  };
  const exactDay = (y, m, d) => {
    const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    if (dt.getUTCMonth() + 1 !== Number(m) || dt.getUTCDate() !== Number(d)) return null;
    return dt.toISOString().slice(0, 10);
  };
  const sep = '[-/. ]';
  let m;
  if ((m = s.match(new RegExp(`^(\\d{4})${sep}(\\d{1,2})${sep}(\\d{1,2})$`)))) return exactDay(m[1], m[2], m[3]);
  if ((m = s.match(new RegExp(`^(\\d{1,2})${sep}(\\d{1,2})${sep}(\\d{4})$`)))) return exactDay(m[3], m[2], m[1]);
  if ((m = s.match(new RegExp(`^(\\d{4})${sep}(\\d{1,2})$`)))) return endOfMonth(m[1], m[2]);
  if ((m = s.match(new RegExp(`^(\\d{1,2})${sep}(\\d{2,4})$`)))) return endOfMonth(m[2], m[1]);
  if ((m = s.match(/^(\d{2})(\d{2}|\d{4})$/))) return endOfMonth(m[2], m[1]);
  if ((m = s.match(new RegExp(`^([a-z]{3,})${sep}?(\\d{2,4})$`, 'i')))) {
    const idx = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase());
    return idx < 0 ? null : endOfMonth(m[2], idx + 1);
  }
  return null;
};

const isMonthOnly = (text) => /^(\d{1,2}|[a-z]{3,})[-/. ]?\d{2,4}$/i.test(text.trim());

/**
 * Keyboard-first expiry field. `onChange` receives the canonical YYYY-MM-DD
 * string, '' when cleared, or null while the typed text cannot be read — so
 * callers can block saving on null.
 */
export const ExpiryDateInput = ({
  value, onChange, className = '', inputClassName = '', compact = false, testId = 'expiry-input',
}) => {
  const [text, setText] = useState(value || '');
  const lastEmitted = useRef(value || '');

  // Follow external resets (new line added, dialog reopened) without fighting
  // the user while they type.
  useEffect(() => {
    const incoming = value || '';
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setText(incoming);
    }
  }, [value]);

  const handle = (raw) => {
    setText(raw);
    const parsed = parseExpiryInput(raw);
    lastEmitted.current = parsed || '';
    onChange(parsed);
  };

  const parsed = parseExpiryInput(text);
  return (
    <div className={className}>
      <Input
        value={text}
        onChange={(e) => handle(e.target.value)}
        placeholder="03/2027, MAR 2027"
        autoComplete="off"
        className={inputClassName}
        data-testid={testId}
      />
      {text.trim() && (parsed ? (
        <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-muted-foreground mt-1`} data-testid={`${testId}-hint`}>
          Expires {fmtDate(parsed)}{isMonthOnly(text) ? ' (end of month)' : ''}
        </p>
      ) : (
        <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-destructive mt-1`} data-testid={`${testId}-error`}>
          Type a month and year like 03/2027 or MAR 2027
        </p>
      ))}
    </div>
  );
};
